import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Form, Formik, FormikTouched, useFormikContext } from 'formik';
import * as yup from 'yup';
import styled from 'styled-components';
import ReCAPTCHA from 'react-google-recaptcha';
import Grid from '@material-ui/core/Grid';
import cloneDeep from 'lodash-es/cloneDeep';
import {
  CustomFieldFilterModelInput,
  FieldConstraintDefinitionModel,
  FieldConstraintModelInput,
  FieldKeyModel
} from '@utmost.co/enterprise-models';
import { ElementQueryResult, ElementWithMeta, SmartFormElements } from '../types';
import { logger } from '../logger';
import { getComponent } from '../elements/getComponent';
import StyledElementMargin from './layouts/ElementMargin';
import { schemaBuilderV2 } from './utils/validation/schemaBuilderV2';
import objectBuilder, { preparePristineFields, sanitizeObjectKeys } from './utils/objectBuilder';
import cleanEmptyValues from './utils/cleanEmptyValues';
import { URLParams } from './utils/URLParams';
import { normalizeModel } from './utils/model/normalizeModel';
import { InputModel, getModelPath, getModelPathFromIndex, objectUtil } from './utils';
import { FormDefaultSubmit } from './SubmitButton';
import { useSmartformContext } from './controllers';
import { preProcessElements } from './utils/preProcessElements';
import { getModelDiff } from './utils/diffMode/getModelDiff';
import { DiffMode } from './utils/diffMode/types';
import { isEmpty } from './utils/isEmpty';

export interface ValueObject {
  [prop: string]: string | number | undefined | null | any[] | this;
}

export interface SmartformProps<Type extends string, Model = InputModel> {
  elements: ElementWithMeta<Model>[];
  useUrlQuery?: () => URLSearchParams;
  onError?: (errors: ValueObject) => void;
  marginStyle?: Partial<React.CSSProperties>;
  onSubmit: (values: DeepPartial<Model>, setSubmitting: (isSubmitting: boolean) => void) => void | Promise<any>;
  initialValues?: DeepPartial<Model>;
  contextMap?: Map<string, object>;
  initialTouched?: FormikTouched<DeepPartial<Model>>;
  formElements: SmartFormElements<Type, Model>;
  actionButtons?: React.ReactNode;
  reCaptcha?: string;
  validate?: boolean | 'edit-mode-only';
  enableReinitialize?: boolean;
  hideEmptyHints?: boolean;
  onChange?: (values: DeepPartial<Model>) => void;
  hidden?: boolean;
  /*! diffMode - Enables a mode that returns through onSubmit/onChange only the part of the Model that actually changed, instead of the entire Model given in initialVales prop */
  diffMode?: DiffMode;
  childrenPlacement?: 'top' | 'bottom';
  patchError?: { code: string; message: string };
  submitted?: boolean;
  /*! watchFieldKeysForBlur - Field keys to watch for blur events. Requires onFieldBlur to also be provided. */
  watchFieldKeysForBlur?: FieldKeyModel[];
  /*! onFieldBlur - Fired when a field whose meta.fieldKey is in watchFieldKeysForBlur transitions to touched.
   * The model path is also passed, since multiple elements (e.g. legal name vs. preferred name) can share the
   * same fieldKey and need to be told apart by consumers. */
  onFieldBlur?: (fieldKey: FieldKeyModel, value: any, path: string) => void;
}

export function Smartform<Type extends string, Model = InputModel>({
  elements,
  onError,
  onSubmit,
  useUrlQuery = () => new URLSearchParams(),
  initialValues,
  contextMap,
  initialTouched,
  children,
  formElements,
  marginStyle,
  actionButtons,
  reCaptcha,
  validate,
  enableReinitialize,
  hideEmptyHints,
  onChange,
  childrenPlacement,
  hidden,
  patchError,
  diffMode,
  submitted,
  watchFieldKeysForBlur,
  onFieldBlur
}: SmartformProps<Type, Model> & { children?: any }) {
  const { previewMode, validator, fieldMeta, constraintsDefinitions, readonly } = useSmartformContext();
  const [schema, setSchema] = useState<yup.ObjectSchema<Record<string, any> | undefined>>();
  const [waitingForCaptcha, setWaitingForCaptcha] = useState(reCaptcha ? true : undefined);
  const urlQuery = useUrlQuery();
  const [urlParams] = useState((values = {}) => {
    urlQuery.forEach((value, key) => {
      if ([URLParams.UID, URLParams.PRIMARY_ORG_NAME, URLParams.PRIMARY_ORG_UID].includes(key as URLParams)) {
        values = objectUtil.setIn(values, key, value);
      }
    });
    return values;
  });

  const [filteredElements, setFilteredElements] = useState(
    preProcessElements(validator, elements, initialValues ?? {}, contextMap, fieldMeta)
  );

  const shouldValidate = validate === undefined || validate === true || (validate === 'edit-mode-only' && !readonly);

  const [initialFilteredElements] = useState(filteredElements);

  useEffect(() => {
    setFilteredElements(preProcessElements(validator, elements, initialValues ?? {}, contextMap, fieldMeta));
  }, [elements, initialValues, validator, contextMap, fieldMeta]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: exception
  useEffect(() => {
    try {
      setSchema(schemaBuilderV2<any>(shouldValidate ? filteredElements : []));
    } catch (e: any) {
      onError?.({ flat: { schemaBuilderError: e.message } });
    }
  }, [JSON.stringify(filteredElements), formElements, onError, shouldValidate]);

  const formValues = useMemo(() => {
    const hasParams = !isEmpty(urlParams ?? {}) || !isEmpty(initialValues ?? {});

    const configInitialValues = objectBuilder<Model>(initialFilteredElements, 'initialValue');

    /**
     * To allow formik to properly map values to the elements and fields, we need to ensure the
     * keys don't have any characters that may confuse it in finding paths.
     */
    const externalInitialValues = sanitizeObjectKeys(initialValues || {});
    const formValues =
      !previewMode && hasParams
        ? { ...configInitialValues, ...externalInitialValues, ...urlParams }
        : configInitialValues;

    return preparePristineFields(formValues, initialFilteredElements);
  }, [initialFilteredElements, initialValues, previewMode, urlParams]);

  const getOutputModel = useCallback(
    (values: any) => {
      const formValues = normalizeModel(values, !!diffMode);
      return diffMode ? getModelDiff(formValues, initialValues, elements, diffMode) : formValues;
    },
    [diffMode, elements, initialValues]
  );

  const handleOnSubmit = useCallback(
    (values: any, { setSubmitting }: any) => {
      onSubmit(getOutputModel(values), setSubmitting);
    },
    [getOutputModel, onSubmit]
  );

  const handleOnChange = useCallback(
    (values: any) => {
      onChange?.(getOutputModel(values));
    },
    [getOutputModel, onChange]
  );

  const getElementByFieldKey = useCallback(
    (fieldKey: FieldKeyModel, customFieldId: string | undefined, values: any, getElementOnlyIfOnSmartform = false) => {
      // First try to get the element that may be in the current smartform using the elements provided
      const element = filteredElements.find(
        (element) =>
          (customFieldId ? element.params?.id === customFieldId : true) &&
          element.meta.find((meta) => meta.fieldKey === fieldKey)
      );
      if (element) {
        const meta = element.meta.find((meta) => meta.fieldKey === fieldKey);

        if (meta) {
          const metaIndex = element.meta.indexOf(meta);
          const field = element.fields[metaIndex];
          const path = getModelPathFromIndex(element, metaIndex);

          return {
            element,
            meta,
            field,
            sanitizedFieldName: path,
            currentValue: objectUtil.getIn(values, path)
          } as ElementQueryResult<Model>;
        }
      }

      if (getElementOnlyIfOnSmartform) return null;

      // The key wasnt found. This means the element is not in the current smartform. So instead, lets go through all
      // elements in the formElements and try to find the element that matches the fieldKey
      const allElements = Array.from(formElements.values());
      for (let i = 0; i < allElements.length; i++) {
        const libElem = allElements[i];
        if (!libElem) break;

        const metaIndex = libElem.meta.findIndex((meta) => meta.fieldKey === fieldKey);
        if (metaIndex !== undefined && metaIndex !== -1) {
          const meta = libElem.meta[metaIndex]!;
          const path = getModelPath(
            meta.model as ValueObject,
            undefined, // There wont be a param.id as there isnt any user data
            meta.fieldKey?.toLowerCase().includes('CUSTOM_FIELD') || false
          );
          return {
            element: null,
            meta,
            field: null,
            sanitizedFieldName: path,
            currentValue: objectUtil.getIn(values, path)
          } as ElementQueryResult<Model>;
        }
      }

      return null;
    },
    [filteredElements, formElements]
  );

  const getConstraintsByFieldKey = useCallback(
    (sourceFieldKey: FieldKeyModel, customFieldId: string | undefined, values: any) => {
      const getConstraintFromDefinition = (
        definition: FieldConstraintDefinitionModel,
        getByConstrainedValue: boolean
      ) => {
        const targetFieldKey = getByConstrainedValue ? definition.constrainedByFieldKey : definition.fieldKey;
        const targetCustomFieldRef = getByConstrainedValue
          ? definition.constrainedByCustomFieldReferenceId
          : definition.customFieldReferenceId;

        // Gets the smartform element that matches this key and ref ID
        const element = getElementByFieldKey(targetFieldKey, targetCustomFieldRef, values, false);
        if (!element) {
          return null;
        }

        const valueAsCustomFieldDef = element?.currentValue as CustomFieldFilterModelInput;

        // Get the current values the smartform has for the given element
        let currentValue = element?.currentValue;
        // Necessary due to a workaround in <entity>FinancialFieldsMetadata, where model points wrongly to parent entity instead of cost center(s)
        if (
          (targetFieldKey.endsWith(`_COST_CENTER`) || targetFieldKey.endsWith(`_COST_CENTERS`)) &&
          element?.element?.type.endsWith('FinancialFields')
        ) {
          currentValue = element?.currentValue?.costCenters ?? element?.currentValue?.costCenter;
        }

        const elementValues = targetCustomFieldRef
          ? valueAsCustomFieldDef?.values || (valueAsCustomFieldDef?.value ? [valueAsCustomFieldDef?.value] : [])
          : currentValue?.uid
            ? [currentValue?.uid]
            : currentValue?.items
              ? currentValue.items.map((item: any) => item.uid)
              : [];

        return {
          key: targetFieldKey,
          customFieldReferenceId: targetCustomFieldRef,
          values: elementValues || [],
          fieldConstraintDefinitionUid: definition.uid
        } as FieldConstraintModelInput;
      };

      const constraintsByFieldKey =
        (constraintsDefinitions
          ?.map((definition) => {
            let definitionMatches = false;

            if (definition.fieldKey === sourceFieldKey) {
              if (
                customFieldId &&
                definition.customFieldReferenceId &&
                customFieldId === definition.customFieldReferenceId
              )
                definitionMatches = true;
              else if (!customFieldId) definitionMatches = true;
            }

            if (definitionMatches) {
              return getConstraintFromDefinition(definition as FieldConstraintDefinitionModel, true);
            }

            return null;
          })
          .filter((constraint) => !!constraint) as FieldConstraintModelInput[]) || [];

      const constraintsByConstrainedKey =
        (constraintsDefinitions
          ?.map((definition) => {
            let definitionMatches = false;

            if (definition.constrainedByFieldKey === sourceFieldKey) {
              if (
                customFieldId &&
                definition.constrainedByCustomFieldReferenceId &&
                customFieldId === definition.constrainedByCustomFieldReferenceId
              )
                definitionMatches = true;
              else if (!customFieldId) definitionMatches = true;
            }

            if (definitionMatches) {
              return getConstraintFromDefinition(definition as FieldConstraintDefinitionModel, false);
            }

            return null;
          })
          .filter((constraint) => !!constraint) as FieldConstraintModelInput[]) || [];

      const constraintsToReturn = [...constraintsByFieldKey, ...constraintsByConstrainedKey];

      return constraintsToReturn?.length ? constraintsToReturn : undefined;
    },
    [constraintsDefinitions, getElementByFieldKey]
  );

  const handleValidate = useCallback(
    async (values: any) => {
      let errors: any = {
        flat: {}
      };
      function addError(name: string, err: string) {
        errors = objectUtil.setIn(errors, name, err);
      }
      await schema
        ?.validate(cleanEmptyValues(values), { abortEarly: false, context: values }) // context gives us possibility access to the parent value when using `yup.when(...)` in validation schema
        .catch((e: yup.ValidationError) => {
          if (Array.isArray(e.inner)) {
            e.inner.forEach((err) => {
              errors.flat[err.path] = err.message;
              addError(`${err.path}`, err.message);
            });
          } else {
            logger.error(e);
          }
        });
      if (!Object.keys(errors.flat).length) {
        delete errors.flat;
      }
      onError?.(errors);

      setFilteredElements(preProcessElements(validator, elements, values, contextMap, fieldMeta));

      return errors;
    },
    [elements, onError, schema, validator, contextMap, fieldMeta]
  );

  if (!schema) {
    return null;
  }

  return (
    <Formik<DeepPartial<Model>>
      enableReinitialize={enableReinitialize || previewMode}
      initialValues={formValues}
      validateOnMount
      validateOnBlur={false}
      validateOnChange
      initialTouched={initialTouched}
      onSubmit={handleOnSubmit}
      validate={shouldValidate ? handleValidate : undefined}
    >
      {({ errors, touched, isValidating, submitCount, values }) => (
        // @ts-ignore this component does not support "children" prop properly
        <StyledForm data-test="form" noValidate>
          <Grid container spacing={2}>
            {!hidden && (
              <>
                {(childrenPlacement === 'top' || !childrenPlacement) && children}
                {filteredElements.map((element, index) => (
                  <StyledElementMargin
                    hidden={element.params?.hidden}
                    item
                    xs={12}
                    style={marginStyle}
                    key={element.type + index}
                    className={`form-element ${element.type} ${element.params?.id ?? ''}`}
                  >
                    {getComponent<Type, Model>(
                      element,
                      touched,
                      errors,
                      isValidating,
                      submitCount > 0 || !!submitted,
                      formElements,
                      (key, customFieldId) => getElementByFieldKey(key, customFieldId, values),
                      (key, customFieldId) => getConstraintsByFieldKey(key, customFieldId, values),
                      hideEmptyHints,
                      patchError
                    )}
                  </StyledElementMargin>
                ))}
                {childrenPlacement === 'bottom' && children}
                {reCaptcha && (
                  <div data-test="captcha-container">
                    <StyledReCaptcha sitekey={reCaptcha} onChange={() => setWaitingForCaptcha(false)} />
                  </div>
                )}
              </>
            )}
            {actionButtons || <FormDefaultSubmit waitingForCaptcha={waitingForCaptcha} />}
            {onChange && <FormikEffect values={values} onChange={handleOnChange} />}
            {onFieldBlur && !!watchFieldKeysForBlur?.length && (
              <FormikBlurEffect
                touched={touched}
                values={values}
                fieldKeys={watchFieldKeysForBlur}
                filteredElements={filteredElements}
                onFieldBlur={onFieldBlur}
              />
            )}
            {typeof submitted === 'boolean' && <FormikSubmittedEffect submitted={submitted} />}
          </Grid>
        </StyledForm>
      )}
    </Formik>
  );
}

interface FormikEffectProps<Type extends string, Model = InputModel> {
  values?: SmartformProps<Type, Model>['initialValues'];
  onChange: (values?: DeepPartial<Model>) => void;
}

const FormikEffect = <Type extends string, Model = InputModel>({
  onChange,
  values
}: FormikEffectProps<Type, Model>) => {
  const [previousValues, setPreviousValues] = useState<DeepPartial<Model> | undefined>();

  useEffect(() => {
    if (JSON.stringify(previousValues) !== JSON.stringify(values)) {
      const cloned = cloneDeep(values);
      onChange(cloned);
      setPreviousValues(cloned);
    }
  }, [onChange, previousValues, values]);

  return null;
};

interface FormikBlurEffectProps<Model = InputModel> {
  touched: FormikTouched<any>;
  values: any;
  fieldKeys: FieldKeyModel[];
  filteredElements: ElementWithMeta<Model>[];
  onFieldBlur: (fieldKey: FieldKeyModel, value: any, path: string) => void;
}

// Recursively walks a `touched` subtree, collecting every boolean leaf keyed by its full path. For a
// scalar-bound field (e.g. phoneNumber, email) `touchedAtPath` is itself a boolean, so this yields one leaf
// at `path` - identical to the old flat check. For an array-of-objects field (e.g. nationalIds, where each
// row has its own country/nationalIdType/nationalIdValue/validUntil/primary sub-paths), this yields one leaf
// per sub-field per row, so each can be diffed independently instead of collapsing the whole array into a
// single "has anything under here ever been touched" boolean that only ever fires once.
const collectTouchedLeaves = (touchedValue: any, path: string, leaves: Record<string, boolean>): void => {
  if (touchedValue === true || touchedValue === false) {
    leaves[path] = touchedValue;
    return;
  }

  if (Array.isArray(touchedValue)) {
    touchedValue.forEach((item, index) => {
      collectTouchedLeaves(item, `${path}[${index}]`, leaves);
    });
    return;
  }

  if (touchedValue && typeof touchedValue === 'object') {
    Object.keys(touchedValue).forEach((key) => {
      collectTouchedLeaves(touchedValue[key], `${path}.${key}`, leaves);
    });
  }
};

const FormikBlurEffect = <Model = InputModel>({
  touched,
  values,
  fieldKeys,
  filteredElements,
  onFieldBlur
}: FormikBlurEffectProps<Model>) => {
  const previousTouchedRef = useRef<Record<string, boolean>>({});

  useEffect(() => {
    filteredElements.forEach((element) => {
      element.meta.forEach((meta, metaIndex) => {
        if (!meta.fieldKey || !fieldKeys.includes(meta.fieldKey) || meta.manualBlurOnly) {
          return;
        }

        const path = getModelPathFromIndex(element, metaIndex);
        if (!path) {
          return;
        }

        const leaves: Record<string, boolean> = {};
        collectTouchedLeaves(objectUtil.getIn(touched, path), path, leaves);

        // For array/object-bound fields (e.g. a FieldArray of rows), meta.blurLeafKey narrows the blur
        // signal to changes on one specific nested field (e.g. 'nationalIdValue') - other sub-fields in the
        // same row (country, id type, primary, etc.) are ignored, even if they do get touched.
        const relevantLeaves = meta.blurLeafKey
          ? Object.fromEntries(Object.entries(leaves).filter(([leafPath]) => leafPath.endsWith(`.${meta.blurLeafKey}`)))
          : leaves;

        let hasNewTransition = false;
        Object.entries(relevantLeaves).forEach(([leafPath, isTouched]) => {
          const wasTouched = !!previousTouchedRef.current[leafPath];
          if (isTouched && !wasTouched) {
            hasNewTransition = true;
          }
          previousTouchedRef.current[leafPath] = isTouched;
        });

        if (hasNewTransition) {
          onFieldBlur(meta.fieldKey!, objectUtil.getIn(values, path), path);
        }
      });
    });
  }, [fieldKeys, filteredElements, onFieldBlur, touched, values]);

  return null;
};

const FormikSubmittedEffect = ({ submitted }: { submitted: boolean }) => {
  const { setFormikState } = useFormikContext();

  useEffect(() => {
    setFormikState((prevState) => {
      return { ...prevState, submitCount: submitted ? (prevState?.submitCount ?? 0) + 1 : 0 };
    });
  }, [submitted, setFormikState]);
  return null;
};

export default Smartform;

export const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  padding: 8px;
`;

export const StyledReCaptcha = styled(ReCAPTCHA)`
  margin-bottom: 32px;
`;
